import { cn } from 'utils/cn';
import { cycleSort } from './sortUtil';

type Props = {
    columnId: string;
    label: React.ReactNode;
    sort: string;
    onSortChange: (next: string) => void;
    align?: 'left' | 'right';
    /** Start the cycle ascending instead of descending — the expected first click for text columns. */
    ascFirst?: boolean;
};

/**
 * Click-to-sort header for the plain tables nested under a list-table row expansion
 * (e.g. tier-2 / tier-3 of Expenses grouped view) and for hand-rolled lists such as the price list
 * selection modal. The caret pair is the same markup and the same colors as the engine-driven
 * header in `DataTable`, so the affordance reads identically wherever it appears.
 *
 * `[font:inherit]` and the border/background resets restate what the global Preflight already
 * gives a raw button. `cursor-pointer` does not: Preflight sets no cursor on buttons, so that
 * one is load-bearing.
 */
const NestedTableSortableHeader = ({ columnId, label, sort, onSortChange, align = 'left', ascFirst }: Props) => {
    const [activeId, activeDir] = sort.split(',');
    const isActive = activeId === columnId;
    const isAsc = isActive && activeDir === 'asc';
    const isDesc = isActive && activeDir === 'desc';

    return (
        <button
            type="button"
            onClick={() => onSortChange(cycleSort(sort, columnId, ascFirst))}
            className={cn(
                'hover:text-primary inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-inherit no-underline select-none [font:inherit]',
                // The new design keeps the caret inside the cell: a label that truncates (TooltipHeader) gives way.
                'new:max-w-full',
                align === 'right' ? 'justify-end' : 'justify-start'
            )}
        >
            <span className="new:min-w-0">{label}</span>
            {/* Inactive carets rest at secondary.light (orange); the active direction switches to
                text.secondary (grey) — same states as DataTable's header caret. */}
            <span aria-hidden className="inline-flex shrink-0 flex-col text-[0.75rem] leading-none">
                <span className={cn(isAsc ? 'text-fg-secondary' : 'text-secondary-light')}>▲</span>
                <span className={cn('-mt-0.5', isDesc ? 'text-fg-secondary' : 'text-secondary-light')}>▼</span>
            </span>
        </button>
    );
};

export default NestedTableSortableHeader;
